import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useRecoilState } from 'recoil';
import { tokenState } from 'redux-recoil-sync.state';
import { APIUtils } from 'commons/utils';
import { URLS } from './urls';
import { QueryConfig } from './react-query-utils';

export const TAGS_CACHE_KEY = 'tags';
const BLOCK_TAG_COUNTS_QUERY_KEY = [TAGS_CACHE_KEY, 'blocks', 'tags', 'list'] as const;

export type BlockTagCounts = Record<string, number>;

type ReplaceBlockTagVariables = {
  tag: string;
  replacementTag: string;
};

function toError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;

    if (typeof responseData === 'string' && responseData.length > 0) {
      return new Error(responseData);
    }

    if (typeof responseData?.message === 'string' && responseData.message.length > 0) {
      return new Error(responseData.message);
    }
  }

  return error instanceof Error ? error : new Error('Unknown error');
}

export function useBlockTagCounts() {
  const [token] = useRecoilState(tokenState);

  return useQuery<BlockTagCounts, Error>(
    BLOCK_TAG_COUNTS_QUERY_KEY,
    async () => {
      const result = await axios.get<BlockTagCounts>(URLS.tags('blocks').list(), APIUtils.tokenHeader(token!));
      return result.data;
    },
    { ...QueryConfig, enabled: !!token },
  );
}

export function useDeleteBlockTagMutation() {
  const queryClient = useQueryClient();
  const [token] = useRecoilState(tokenState);

  return useMutation<void, Error, string>({
    mutationFn: async (tag) => {
      try {
        await axios.delete(URLS.tags('blocks').delete(tag), APIUtils.tokenHeader(token!));
      } catch (error) {
        throw toError(error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(BLOCK_TAG_COUNTS_QUERY_KEY);
    },
  });
}

export function useReplaceBlockTagMutation() {
  const queryClient = useQueryClient();
  const [token] = useRecoilState(tokenState);

  return useMutation<void, Error, ReplaceBlockTagVariables>({
    mutationFn: async ({ tag, replacementTag }) => {
      try {
        await axios.post(URLS.tags('blocks').replace(tag), { replacementTag }, APIUtils.tokenHeader(token!));
      } catch (error) {
        throw toError(error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(BLOCK_TAG_COUNTS_QUERY_KEY);
    },
  });
}
